import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
    BUTTON_VARIANT_TYPES,
    EMPTY_FIELDS,
    NO_ACCESS_TYPES,
    PASSWORDS_MISMATCH,
    PERMISSIONS
} from "../../../../utils/constants";
import {
    SaveIcon,
    AtSymbolIcon,
    UserIcon,
    PencilIcon,
    LockClosedIcon,
    LockOpenIcon,
    DeviceMobileIcon,
    TagIcon
} from '@heroicons/react/solid'

import {
    getUserPermissions,
    getAllUserGroups,
    editUser,
    assignUserGroupsToUser,
    getOrganisationApplications,
    editUserPassword,
    unlockUserAccount,
    editUserTags,
    getUserDetails,
    editUserApplications,
    editUserPermissions
} from "../../../../utils/api";

import Button from "../../../SharedComponents/Button";
import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import DualList from "../../../SharedComponents/Form/DualList";
import Input from "../../../Auth/Input";
import Tag from "../../../SharedComponents/Tag";
import DualistPlaceholder from "../../../SharedComponents/Loading/DualistPlaceholder";
import EditUserMenu from "./EditUserMenu";
import AccessControl from "../../../SharedComponents/AccessControl";
import useMountStatus from '../../../../hooks/useMountStatus';
import SectionPlaceholder from "../../../SharedComponents/Loading/SectionPlaceholder";

function EditUser() {
    const [alert, setAlert] = useState('');
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [userGroups, setUserGroups] = useState();
    const [permissions, setPermissions] = useState([]);
    const [applications, setApplications] = useState([]);
    const [user, setUser] = useState();
    const [firstName, setFirstName] = useState();
    const [lastName, setLastName] = useState();
    const [emailAddress, setEmailAddress] = useState();
    const [mobileNumber, setMobileNumber] = useState();
    const [password, setPassword] = useState();
    const [confirmPassword, setConfirmPassword] = useState();
    const [current, setCurrent] = useState();
    const [options, setOptions] = useState();
    const [editOption, setEditOption] = useState("Details");
    const [handleSave, setHandleSave] = useState();
    const [changesMade, setChangesMade] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showPermissionsTabs, setShowPermissionsTabs] = useState(false);
    const [showApplicationsTabs, setShowApplicationsTabs] = useState(false);
    const [showPermissionsDropDownTabs, setShowPermissionsDropDownTabs] = useState(false);
    const [showApplicationsDropDownTabs, setShowApplicationsDropDownTabs] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const [userTags, setUserTags] = useState([]);
    const [showTagsTabs, setShowTagsTabs] = useState(false);
    const [showTagsDropDownTabs, setShowTagsDropDownTabs] = useState(false);

    const unmounted = useMountStatus();

    const location = useLocation();
    const navigate = useNavigate()

    const handleUnlockAccount = async () => {
        setSubmitLoading(true);
        try {
            await unlockUserAccount(user.userIdentifier);
            await handleSuccessfulEdit("You've successfully unlocked " + user.name + "'s account", user.userIdentifier);
        } catch (err) {
            setAlertType(ALERT_TYPE.ERROR);
            setAlert(err.message);
        }
        setSubmitLoading(false);
    }

    const isInputValid = () => {
        if (password === "" || confirmPassword === "") {
            setAlert(EMPTY_FIELDS);

            return false
        }

        if (password !== confirmPassword) {
            setAlert(PASSWORDS_MISMATCH);

            return false
        }

        return true
    }

    const handleChangePassword = async () => {
        if (isInputValid()) {
            setSubmitLoading(true);
            try {
                await editUserPassword(user.userIdentifier, password);
                await handleSuccessfulEdit("You've successfully changed " + user.name + "'s password", user.userIdentifier);

            } catch (err) {
                setAlertType(ALERT_TYPE.ERROR);
                setAlert(err.message);
            }
            setSubmitLoading(false)
        }
    }

    const handleShowPermissionsTabClick = () => {
        setShowPermissionsDropDownTabs(true);
        setShowPermissionsTabs(showPermissionsTabs => !showPermissionsTabs);

        showApplicationsTabs && setShowApplicationsTabs(false);
        showApplicationsDropDownTabs && setShowApplicationsDropDownTabs(false);
        showTagsTabs && setShowTagsTabs(false);
        showTagsDropDownTabs && setShowTagsDropDownTabs(false);
    }

    const handleShowApplicationsTabClick = () => {
        setShowApplicationsDropDownTabs(true);
        setShowApplicationsTabs(showApplicationsTabs => !showApplicationsTabs);

        showPermissionsTabs && setShowPermissionsTabs(false);
        showPermissionsDropDownTabs && setShowPermissionsDropDownTabs(false);
        showTagsTabs && setShowTagsTabs(false);
        showTagsDropDownTabs && setShowTagsDropDownTabs(false);
    }

    const handleShowTagsTabClick = () => {
        setShowTagsDropDownTabs(true);
        setShowTagsTabs(showTagsTabs => !showTagsTabs);

        showApplicationsTabs && setShowApplicationsTabs(false);
        showApplicationsDropDownTabs && setShowApplicationsDropDownTabs(false);
        showPermissionsTabs && setShowPermissionsTabs(false);
        showPermissionsDropDownTabs && setShowPermissionsDropDownTabs(false);
    }

    const handleSuccessfulEdit = async (message, userIdentifier) => {
        setAlert(message);
        setAlertType(ALERT_TYPE.SUCCESS);

        await fetchData(userIdentifier ? userIdentifier : user.userIdentifier, editOption);
    }

    const handleEditUser = async (mobileNumber, email, firstname, lastname, userIdentifier) => {
        setSubmitLoading(true);
        try {
            await editUser(mobileNumber, email, firstname, lastname, userIdentifier);
            await handleSuccessfulEdit("You've successfully edited " + user.name + "'s details", user.userIdentifier);
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleAssignUserGroup = async (userGroups) => {
        setSubmitLoading(true);
        try {
            await assignUserGroupsToUser(user.userIdentifier, userGroups);
            await handleSuccessfulEdit("You've successfully assigned a User Groups to " + user.name, user.userIdentifier);
        } catch (err) {
            setAlertType(ALERT_TYPE.ERROR);
            setAlert(err.message);
        }
        setSubmitLoading(false);
    }

    const handleEditOption = (userEditOption, options, current) => {

        switch (userEditOption ? userEditOption : editOption) {
            case "Details":
                setShowPermissionsTabs(false)
                setShowApplicationsTabs(false)
                setShowTagsTabs(false)
                break;
            case "Change password":
                setShowPermissionsTabs(false)
                setShowApplicationsTabs(false)
                setShowTagsTabs(false)
                break;
            case "Account":
                setShowPermissionsTabs(false)
                setShowApplicationsTabs(false)
                setShowTagsTabs(false)
                break;
            case "User Groups":
                setShowPermissionsTabs(false)
                setShowApplicationsTabs(false)
                setShowTagsTabs(false)
                setCurrent((current ? current : user.userGroups));
                setHandleSave(() => handleAssignUserGroup);
                setOptions((options ? options : userGroups)
                    .filter(optionsItem => !(current ? current : user.userGroups)
                        .find(selectedItem => (selectedItem === optionsItem))));
                break;
            case "permissions":
            case "User Group Permissions":
                setShowPermissionsTabs(true)
                setShowPermissionsDropDownTabs(false)
                break;
            case "Direct Permissions":
                setShowPermissionsTabs(true)
                setShowPermissionsDropDownTabs(false)
                setCurrent((current ? current : user.directPermissions));
                setHandleSave(null);
                setOptions((options ? options : permissions)
                    .filter(optionsItem => !(current ? current : user.directPermissions)
                        .find(selectedItem => (selectedItem.code === optionsItem.code))));
                break;
            case "applications":
            case "User Group Applications":
                setShowApplicationsTabs(true)
                setShowApplicationsDropDownTabs(false)
                break;
            case "Direct Applications":
                setShowApplicationsTabs(true)
                setShowApplicationsDropDownTabs(false)
                setCurrent((current ? current : user.directApplications));
                setHandleSave(null);
                setOptions((options ? options : applications)
                    .filter(optionsItem => !(current ? current : user.directApplications)
                        .find(selectedItem => (selectedItem.name === optionsItem.name))));
                break;
            case "Tags":
            case "User Group Tags":
                setShowTagsTabs(true)
                setShowTagsDropDownTabs(false)
                break;
            case "Direct Tags":
                setShowTagsTabs(true)
                setShowTagsDropDownTabs(false)
                setHandleSave(null);
                break;
            default:
                break;
        }
    }

    const handleEditOptionClick = (editOption) => {
        editOption && setEditOption(editOption);
        handleEditOption(editOption)
    };

    const handleEditPermissions = async (userIdentifier, current) => {
        setSubmitLoading(true);
        try {
            editUserPermissions(userIdentifier, current.map(permission => permission.code));
            await handleSuccessfulEdit("You've successfully updated user permissions");
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleEditApplications = async (userIdentifier, current) => {
        setSubmitLoading(true);
        try {
            editUserApplications(userIdentifier, current.map(application => application.identifier));
            await handleSuccessfulEdit("You've successfully updated user applications");
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleEditTags = async (userIdentifier, userTags) => {
        setSubmitLoading(true);
        try {
            editUserTags(userIdentifier, userTags);
            await handleSuccessfulEdit(
                "You've successfully updated user tags"
            );
        } catch (err) {
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setSubmitLoading(false);
    }

    const handleTagsKeyPress = (key) => {
        if (key === 'Enter' && tagInput.length > 1) {
            setUserTags(tags => [...new Set([...tags, tagInput.toLowerCase()])]);
            setTagInput("");
            setChangesMade(true);
        }
    }

    const deleteTag = (tagToDelete) => {
        setUserTags(tags => tags.filter(tag => tag !== tagToDelete));
        setChangesMade(true);
    }

    const fetchData = async (userIdentifier, editOption) => {
        setLoading(true);

        try {
            const userGroupsResponse = await getAllUserGroups();
            const permissionsResponse = await getUserPermissions();
            const applicationsResponse = await getOrganisationApplications();
            const user = await getUserDetails(userIdentifier);

            if (unmounted) return;
            setUserGroups(userGroupsResponse.map(userGroup => userGroup.name));
            setPermissions(permissionsResponse);
            setApplications(applicationsResponse);
            setUser(user);
            if (!user.directPermissions) {
                user.directPermissions = [];
            }
            if (!user.directApplications) {
                user.directApplications = [];
            }
            setFirstName(user.name.split(" ")[0]);
            setLastName(user.name.split(" ")[1]);
            setEmailAddress(user.emailAddress);
            setMobileNumber(user.mobileNumber);
            setUserTags(user.directTags);

            switch (editOption) {
                case "Direct Applications":
                    handleEditOption(editOption, applicationsResponse, user.directApplications);
                    break;
                case "Direct Permissions":
                    handleEditOption(editOption, permissionsResponse, user.directPermissions);
                    break;
                case "User Groups":
                    handleEditOption(editOption, userGroupsResponse.map(userGroup => userGroup.name), user.userGroups);
                    break;
                default:
                    handleEditOption(editOption);
                    break;
            }
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLoading(false);
    };

    const handleOnClickSave = () => {
        if (editOption === "Details") {
            handleEditUser(mobileNumber, emailAddress, firstName, lastName, user.userIdentifier);
        } else if (editOption === "Change password") {
            handleChangePassword(user.userIdentifier, password);
        } else if (editOption === "Account") {
            handleUnlockAccount(user.userIdentifier);
        } else if (editOption === "Direct Permissions") {
            handleEditPermissions(user.userIdentifier, current);
        } else if (editOption === "Direct Applications") {
            handleEditApplications(user.userIdentifier, current);
        } else if (editOption === "Direct Tags") {
            handleEditTags(user.userIdentifier, userTags);
        } else {
            handleSave(current);
        }
    }

    const handleGoToEditUserGroup = (userGroup, editOption) => {
        navigate("/user-groups/edit-user-group", {
            state: {
                userGroupName: userGroup,
                editOption: editOption
            }
        });
    }

    useEffect(() => {
        location.state.editOption && setEditOption(location.state.editOption);
        fetchData(location.state.userIdentifier, location.state.editOption ? location.state.editOption : "Details");

        // Some refactoring is needed here, not urgent but there's useCallback/dependency issues with other functions
        // (can't use some functions before initiation) once we add fetchData to dependencies
        /* eslint-disable */
    }, [location.state.editOption, location.state.userIdentifier]);
    /* eslint-enable */

    return (
        <AccessControl requiredPermission={PERMISSIONS.UPDATE_USER_DETAILS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer title={"Edit User"} titleIcon={<UserIcon className="block h-5 w-5 mr-2"/>}>
                <Alert
                    text={alert}
                    type={alertType}
                    active={alert}
                    close={() => setAlert("")}
                />
                <div className="grid grid-flow-row xl:grid-cols-8 lg:grid-cols-2 gap-6 min-h-0 overflow-x-scroll">
                    <EditUserMenu
                        user={user}
                        editOption={editOption}
                        handleEditOptionClick={handleEditOptionClick}
                        showPermissionsTabs={showPermissionsTabs}
                        showPermissionsDropDownTabs={showPermissionsDropDownTabs}
                        handleShowPermissionsTabClick={handleShowPermissionsTabClick}
                        showApplicationsTabs={showApplicationsTabs}
                        showApplicationsDropDownTabs={showApplicationsDropDownTabs}
                        handleShowApplicationsTabClick={handleShowApplicationsTabClick}
                        showTagsTabs={showTagsTabs}
                        showTagsDropDownTabs={showTagsDropDownTabs}
                        handleShowTagsTabClick={handleShowTagsTabClick}
                        loading={loading}
                    />
                    <div className={"xl:col-span-6 col-span-2"}>
                        <div className={
                            "border-0 rounded-lg relative flex flex-col w-full bg-white outline-none " +
                            "focus:outline-none shadow-sm"
                        }>
                            <div className="flex items-start justify-between pt-4 pb-2 px-4 rounded-t">
                                <h3 className="text-md font-medium leading-6 text-indigo-900 ">
                                    {loading ?
                                        <div
                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-48"}/>
                                        :
                                        editOption === "Change password" ?
                                            <span className="flex">Change <div
                                                className="ml-1 text-secondary-600">{(user && user.name)}</div>'s password</span>
                                            :
                                            editOption === "Account" ?
                                                <span className="flex">Unlock <div
                                                    className="ml-1 text-secondary-600">{(user && user.name)}</div>'s account</span>
                                                :
                                                <span className="flex">Edit <div
                                                    className="ml-1 text-secondary-600">{(user && user.name)}</div>'s {editOption}</span>
                                    }
                                </h3>
                            </div>
                            <div className="px-4">
                                {loading ?
                                    <div
                                        className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-2/4"}/>
                                    :
                                    <p className="text-sm text-gray-600">
                                        {editOption === "Account" ?
                                            user && user.isLocked ?
                                                (user && user.name) + "'s account is currently locked. Click the button below to restore access to account."
                                                :
                                                (user && user.name) + "'s account is currently unlocked."
                                            :
                                            editOption === "Change password" ?
                                                "Use the form below to change " + (user && user.name) + "'s password"
                                                :
                                                "Use the form below to add or remove " + editOption + " from " + (user && user.name)
                                        }
                                    </p>
                                }
                                {(editOption === "Direct Permissions" || editOption === "Direct Applications") &&
                                    <div className={
                                        "text-sm mt-3 w-full mx-auto rounded-md px-3 py-3 info " +
                                        "bg-amber-100 text-amber-700 align-center justify-center"
                                    }>
                                        We strongly recommend editing permissions and applications using
                                        <span className={"font-semibold"}> User Groups</span> rather than directly for
                                        each
                                        user.
                                    </div>
                                }

                                {editOption === "Account" &&
                                    <div className={
                                        "text-sm mt-3 w-full mx-auto rounded-md px-3 py-3 info " +
                                        "bg-amber-100 text-amber-700 align-center justify-center"
                                    }>
                                        As a security measure user's accounts are locked after a number of incorrect
                                        repeated attempts to login to Psych Press Portal
                                    </div>
                                }

                            </div>
                            <div className="relative px-4 flex-auto mt-4 mb-4">

                                {editOption === "Details" &&
                                    (loading ?
                                            <SectionPlaceholder/>
                                            :
                                            <>
                                                <div className={"grid grid-flow-row gap-4 mb-8"}>
                                                    <div className={"grid grid-flow-row xl:grid-flow-col gap-4"}>
                                                        <Input
                                                            optional
                                                            type="text"
                                                            name="First name"
                                                            id="firstname"
                                                            placeholder={firstName}
                                                            onChange={(textInput) => {
                                                                setFirstName(textInput);
                                                                setChangesMade(true);
                                                            }}
                                                            Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                        />
                                                        <Input
                                                            optional
                                                            type="text"
                                                            name="Last name"
                                                            id="lastname"
                                                            placeholder={lastName}
                                                            onChange={(textInput) => {
                                                                setLastName(textInput);
                                                                setChangesMade(true);
                                                            }}
                                                            Icon={<UserIcon className="h-4 w-4 text-gray-500"/>}
                                                        />
                                                    </div>
                                                    <div className={"grid grid-flow-row gap-4"}>
                                                        <div className={"xl:col-span-1 col-span-full"}>
                                                            <Input
                                                                id="email"
                                                                type="email"
                                                                name="Email"
                                                                placeholder={emailAddress}
                                                                onChange={(textInput) => {
                                                                    setEmailAddress(textInput);
                                                                    setChangesMade(true);
                                                                }}
                                                                Icon={<AtSymbolIcon className="h-4 w-4 text-gray-500"/>}
                                                            />
                                                        </div>
                                                        <div className={"xl:col-span-1 col-span-full"}>
                                                            <Input
                                                                optional
                                                                id="mobile"
                                                                type="text"
                                                                name="Mobile"
                                                                placeholder={mobileNumber}
                                                                onChange={(textInput) => {
                                                                    setMobileNumber(textInput);
                                                                    setChangesMade(true);
                                                                }}
                                                                Icon={<DeviceMobileIcon
                                                                    className="h-4 w-4 text-gray-500"/>}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                    )
                                }
                                {editOption === "Change password" &&
                                    (loading ?
                                            <SectionPlaceholder/>
                                            :
                                            <>
                                                <div className={"grid grid-flow-col gap-4"}>
                                                    <Input
                                                        type="password"
                                                        name="New password"
                                                        id="password"
                                                        placeholder={"New password"}
                                                        onChange={(textInput) => {
                                                            setPassword(textInput);
                                                            setChangesMade(true);
                                                        }}
                                                        Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                                                    />
                                                    <Input
                                                        type="password"
                                                        name="Confirm new password"
                                                        id="confirm-password"
                                                        placeholder={"Confirm new password"}
                                                        onChange={(textInput) => {
                                                            setConfirmPassword(textInput);
                                                            setChangesMade(true);
                                                        }}
                                                        Icon={<LockClosedIcon className="h-4 w-4 text-gray-500"/>}
                                                    />
                                                </div>
                                            </>
                                    )
                                }
                                {(editOption === "User Groups" || editOption === "Direct Permissions"
                                    || editOption === "Direct Applications") ?
                                    loading ?
                                        <DualistPlaceholder/>
                                        :
                                        options && current &&
                                        <>
                                            <DualList
                                                options={options}
                                                setOptions={setOptions}
                                                current={current}
                                                setCurrent={setCurrent}
                                                itemName={editOption}
                                                handleSave={handleSave}
                                                setChangesMade={setChangesMade}
                                            />

                                            <div className={
                                                "text-sm mt-4 w-full mx-auto rounded-md px-3 py-3 info bg-amber-100 " +
                                                "text-amber-700 align-center justify-center"
                                            }>
                                                Please note that this does not remove {editOption} from the system
                                            </div>
                                        </>
                                    :
                                    (editOption === "User Group Permissions" || editOption === "User Group Applications" || editOption === "User Group Tags") &&
                                    <>
                                        <div className={
                                            "text-sm text-sm font-medium text-indigo-900 w-full mb-2 grid grid-flow-col " +
                                            "space-between"
                                        }>
                                            {user.name} is currently part of {user.userGroups.length} user
                                            group{user.userGroups.length > 1 && "s"}:
                                        </div>
                                        <div className="grid grid-flow-row gap-2 mt-4">
                                            <div className="overflow-hidden border-b border-none mt-4">
                                                <table className="table-fixed min-w-full">
                                                    <thead className="bg-blue-gray-50">
                                                    <tr>
                                                        <th scope="col"
                                                            className="w-1/2 px-6 py-3 text-left text-xs font-medium
                                                            text-blue-gray-500 uppercase font-mono tracking-wider">
                                                            User Group
                                                        </th>
                                                        <th scope="col"
                                                            className="w-1/2 px-6 py-3 text-left text-xs font-medium
                                                            text-blue-gray-500 uppercase font-mono tracking-wider">
                                                            Edit {editOption === "User Group Permissions" ?
                                                            "Permissions" : editOption === "User Group Applications" ? "Applications" : "Tags"}
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody className="bg-white">
                                                    {user.userGroups.map((userGroup, index) => (
                                                        <tr key={index}>
                                                            <td className="px-6 py-4">
                                                                <div className="flex space-between">
                                                                    <div className="text-sm text-blue-gray-500">
                                                                        {userGroup}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <Button
                                                                    iconPositionLeft
                                                                    icon={<PencilIcon
                                                                        className="block h-3.5 w-3.5 mr-1"/>}
                                                                    colour={BUTTON_VARIANT_TYPES.UNCOLOURED}
                                                                    text="Edit"
                                                                    extraStyling="shadow-none text-primary-600 p-0 focus:ring-0 focus:transparent"
                                                                    onClick={() => handleGoToEditUserGroup(userGroup, editOption === "User Group Permissions" ?
                                                                        "permissions" : editOption === "User Group Application" ? "applications" : "tags")}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </>
                                }
                                {editOption === "Direct Tags" &&
                                    (loading ?
                                            <>
                                                <div className={"grid grid-flow-col gap-4"}>
                                                    <div>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg h-4 w-2/4"}/>
                                                        <div
                                                            className={"animate-pulse px-4 py-3 h-5 bg-blue-gray-200 rounded-lg w-full h-9 mt-2"}/>
                                                    </div>
                                                </div>
                                            </>
                                            :
                                            <>
                                                <Input
                                                    id="tags"
                                                    type="text"
                                                    name="Tags"
                                                    placeholder="Enter tag and press enter key"
                                                    value={tagInput}
                                                    onChange={setTagInput}
                                                    Icon={<TagIcon className="h-4 w-4 text-gray-500"/>}
                                                    onKeyPress={handleTagsKeyPress}
                                                />
                                                <div className="flex flex-wrap">
                                                    {userTags.map(tag => <Tag key={tag} text={tag}
                                                                              extraStyling="mr-2 mt-4"
                                                                              onDelete={() => deleteTag(tag)}/>)}
                                                </div>
                                            </>
                                    )
                                }

                            </div>
                            <div className={
                                "p-4 bg-gradient-to-l from-primary-50 to-secondary-50 flex items-center justify-end " +
                                "text-right rounded-md"
                            }>
                                <div className="flex flex-row gap-3">
                                    {user && editOption === "Account" ?
                                        <Button
                                            text={"Unlock " + user.name + "'s account"}
                                            icon={<LockOpenIcon className="block h-4 w-4 mr-2"/>}
                                            iconPositionLeft
                                            onClick={handleOnClickSave}
                                            loading={submitLoading}
                                            disabled={loading || !user.isLocked}
                                            extraStyling={
                                                "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                                "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                            }
                                        />
                                        : user && !editOption.includes("User Group ") ?
                                            <Button
                                                text="Save changes"
                                                icon={<SaveIcon className="block h-4 w-4 mr-2"/>}
                                                iconPositionLeft
                                                onClick={handleOnClickSave}
                                                loading={submitLoading}
                                                disabled={loading || !changesMade}
                                                extraStyling={
                                                    "text-white focus:ring-inset focus:ring-1 bg-secondary-500 " +
                                                    "hover:bg-secondary-600 focus:outline-none focus:ring-secondary-500"
                                                }
                                            />
                                            : <></>
                                    }

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DashboardContainer>
        </AccessControl>
    )
}

export default EditUser;
